/** Copyable Nightshift MCP install snippets. Browser-safe; no Node APIs. */

export type McpClientId =
  | 'prompt'
  | 'cursor'
  | 'codex'
  | 'claude-code'
  | 'claude-desktop'
  | 'grok'
  | 'kimi-code'
  | 'kimi-cli';

type McpSnippetFormat = 'prompt' | 'json' | 'toml';

export interface McpSnippetInput {
  origin: string;
  token: string;
  permission?: 'read' | 'write';
}

export interface McpClientSnippet {
  id: McpClientId;
  label: string;
  format: McpSnippetFormat;
  /** Where to paste this block. */
  files: string[];
  notes: string;
  /** Optional CLI that writes the same config. */
  command?: string;
  body: string;
}

export const DEFAULT_MCP_CLIENT: McpClientId = 'prompt';
export const PLACEHOLDER_TOKEN = 'YOUR_NIGHTSHIFT_TOKEN';

function originOf(origin: string): string {
  return origin.replace(/\/+$/, '');
}

function mcpUrl(origin: string): string {
  return `${originOf(origin)}/api/mcp`;
}

function jsonBlock(entry: Record<string, unknown>): string {
  return `${JSON.stringify({ mcpServers: { nightshift: entry } }, null, 2)}\n`;
}

function tomlHttp(origin: string, token: string, headersKey: 'http_headers' | 'headers'): string {
  const url = mcpUrl(origin);
  return `[mcp_servers.nightshift]
url = "${url}"
${headersKey} = { Authorization = "Bearer ${token}" }
`;
}

export function setupPrompt(input: McpSnippetInput): string {
  const origin = originOf(input.origin);
  const url = mcpUrl(origin);
  const token = input.token;
  const permission = input.permission ?? 'write';
  return `Set yourself up as a Nightshift factory agent on this machine.

Origin: ${origin}
MCP: POST ${url}
Handshake: GET ${url}
Authorization: Bearer ${token}
Token permission: ${permission} (${permission === 'write' ? 'read plus ticket commands' : 'query only'})

Public skill pack (no token):
  ${origin}/skills/
  ${origin}/skills/nightshift/SKILL.md
  ${origin}/skills/nightshift-new-ticket/SKILL.md
  ${origin}/skills/nightshift-epic/SKILL.md
  ${origin}/skills/nightshift-skills-version/SKILL.md
  ${origin}/skills/nightshift-skills-update/SKILL.md
  ${origin}/skills/nightshift-skills-update/scripts/sync.mjs
  ${origin}/skills/catalogue.json
  ${origin}/skills/manifest.json
  ${origin}/skills/nightshift/scripts/mcp-stdio.mjs

Do this:
1. Fetch the skill pack URLs above and follow them.
2. Install every skill listed in catalogue.json (nightshift, nightshift-new-ticket, nightshift-epic, nightshift-skills-version, nightshift-skills-update) with their scripts, byte-for-byte, in this client's usual skill directory. Easiest: download nightshift-skills-update/scripts/sync.mjs and run: node sync.mjs update. Do not replace skills with summaries.
3. Register an MCP server named nightshift that talks to POST ${url} with the bearer token. Use this client's native config format (below). Merge with existing servers; do not replace the whole file.
4. Call the status tool. If it fails, stop and tell the user.

Do not scrape the dashboard. Merge, budget reset, and user admin are not MCP tools.

Client config (pick the one that matches you):
- Cursor — ~/.cursor/mcp.json or project .cursor/mcp.json — JSON, url + headers.Authorization
- Codex CLI and Codex Desktop — same file ~/.codex/config.toml — TOML, [mcp_servers.nightshift], url + http_headers (not JSON)
- Claude Code / Claude CLI — ~/.claude.json (user) or project .mcp.json — JSON, type must be "http", plus url + headers. Not ~/.claude/settings.json. Command: claude mcp add --transport http nightshift ${url} --header "Authorization: Bearer ${token}"
- Claude Desktop — %APPDATA%\\Claude\\claude_desktop_config.json (Windows) or ~/Library/Application Support/Claude/claude_desktop_config.json (macOS) — stdio only. Download mcp-stdio.mjs, spawn node with env NIGHTSHIFT_URL=${origin} and NIGHTSHIFT_TOKEN=${token}. Do not put url in this file.
- Grok CLI — ~/.grok/config.toml — TOML, [mcp_servers.nightshift], url + headers. Command: grok mcp add --transport http nightshift ${url} --header "Authorization: Bearer ${token}"
- Kimi Code — ~/.kimi-code/mcp.json or project .kimi-code/mcp.json — JSON, url + headers. TUI: /mcp-config
- Kimi CLI (legacy) — ~/.kimi/mcp.json — same JSON as Kimi Code

If unsure, fetch ${origin}/skills/ and use the snippet for this client.
`;
}

export function mcpClients(input: McpSnippetInput): McpClientSnippet[] {
  const origin = originOf(input.origin);
  const token = input.token;
  const url = mcpUrl(origin);
  const httpJson = jsonBlock({
    url,
    headers: { Authorization: `Bearer ${token}` },
  });
  const claudeCodeJson = jsonBlock({
    type: 'http',
    url,
    headers: { Authorization: `Bearer ${token}` },
  });
  const desktopJson = jsonBlock({
    command: 'node',
    args: ['/ABSOLUTE/PATH/TO/mcp-stdio.mjs'],
    env: {
      NIGHTSHIFT_URL: origin,
      NIGHTSHIFT_TOKEN: token,
    },
  });

  return [
    {
      id: 'prompt',
      label: 'Setup prompt',
      format: 'prompt',
      files: [],
      notes: 'Paste this to any agent and let it configure itself. Default option.',
      body: setupPrompt(input),
    },
    {
      id: 'cursor',
      label: 'Cursor',
      format: 'json',
      files: ['~/.cursor/mcp.json', 'project .cursor/mcp.json'],
      notes: 'Merge the nightshift key into mcpServers. Restart Cursor after saving.',
      body: httpJson,
    },
    {
      id: 'codex',
      label: 'Codex',
      format: 'toml',
      files: ['~/.codex/config.toml'],
      notes:
        'CLI and desktop share this TOML file — not JSON. Desktop ignores project .codex/config.toml, so put it here. Append the table; do not replace the file. The CLI command needs NIGHTSHIFT_TOKEN in the environment; the block below inlines the header instead.',
      command: `codex mcp add nightshift --url ${url} --bearer-token-env-var NIGHTSHIFT_TOKEN`,
      body: tomlHttp(origin, token, 'http_headers'),
    },
    {
      id: 'claude-code',
      label: 'Claude Code',
      format: 'json',
      files: ['~/.claude.json', 'project .mcp.json'],
      notes:
        'type must be "http". A url with no type is read as stdio and skipped. Do not put this in ~/.claude/settings.json.',
      command: `claude mcp add --transport http nightshift ${url} --header "Authorization: Bearer ${token}"`,
      body: claudeCodeJson,
    },
    {
      id: 'claude-desktop',
      label: 'Claude Desktop',
      format: 'json',
      files: [
        '%APPDATA%\\Claude\\claude_desktop_config.json',
        '~/Library/Application Support/Claude/claude_desktop_config.json',
      ],
      notes:
        'Desktop config is stdio-only. Download mcp-stdio.mjs from the skill pack, put an absolute path in args, then fully quit and reopen Claude. Connectors UI is OAuth-only and cannot send this bearer token.',
      body: desktopJson,
    },
    {
      id: 'grok',
      label: 'Grok',
      format: 'toml',
      files: ['~/.grok/config.toml'],
      notes: 'Official Grok CLI. This is TOML, not JSON. ${VAR} in headers expands from the environment if you prefer not to paste the token.',
      command: `grok mcp add --transport http nightshift ${url} --header "Authorization: Bearer ${token}"`,
      body: tomlHttp(origin, token, 'headers'),
    },
    {
      id: 'kimi-code',
      label: 'Kimi Code',
      format: 'json',
      files: ['~/.kimi-code/mcp.json', 'project .kimi-code/mcp.json'],
      notes: 'url without transport is HTTP. TUI /mcp-config can paste the same block.',
      body: httpJson,
    },
    {
      id: 'kimi-cli',
      label: 'Kimi CLI',
      format: 'json',
      files: ['~/.kimi/mcp.json'],
      notes: 'Legacy kimi-cli. Same JSON shape as Kimi Code, different path.',
      body: httpJson,
    },
  ];
}

export function mcpClient(id: McpClientId, input: McpSnippetInput): McpClientSnippet {
  const found = mcpClients(input).find((entry) => entry.id === id);
  if (!found) throw new Error(`unknown MCP client ${id}`);
  return found;
}
